import type React from "react";
import { get } from "lodash";
import { useEventInstance, useYearRangeToggle } from "@/widgets/classic-form/form";
import { BirthYearCalculator } from "@/features/birth-year-calculator";
import { FieldComposer, fieldToConfig } from "@/features/form-fields";
import type { SpecialFieldItem } from "@/entities/form-metadata";
import { UserActionControl } from "@/entities/form-metadata";
import { Grid } from "@/shared/components/Grid";
import { useFormStore } from "@/shared/forms";
import { useCSSSafeId } from "@/shared/hooks/useCSSSafeId";
import styles from "./BirthYearField.module.scss";

interface YearFieldProps {
    birthYearField: SpecialFieldItem;
}

export const BirthYearField: React.FC<YearFieldProps> = ({ birthYearField }) => {
    const { fields } = birthYearField;
    const yearField = fields?.[0];

    const { instancePath, dateFieldPath, dateProximityPath, setYear, clearYear } = useEventInstance({
        eventName: "SelfBirth",
    });

    const { isRangeMode } = useYearRangeToggle(instancePath);

    const fieldHtmlId = useCSSSafeId();
    // Read the shared value via subscription instead of mounting a `form.Field`. A second
    // `form.Field` on this path (the real event field uses it too) would, in edit mode,
    // re-seed itself from `defaultValues` the instant the instance is removed — resurrecting
    // a just-removed birth event. Writes still go through `useEventInstance` below.
    const yearValue = useFormStore((state) => get(state.values, dateFieldPath));

    if (!yearField) {
        return null;
    }

    const fieldConfig = fieldToConfig(yearField);
    const userActionControls = yearField.meta?.userActionControls;
    const hasCalc = userActionControls?.includes(UserActionControl.Calc);

    const setBirthYear = (year: number | null) => {
        if (year === null) {
            clearYear();
        } else {
            setYear(year);
        }
    };

    return (
        <Grid.Field spanDesktop={3} spanTablet={4} spanMobile={12}>
            <FieldComposer
                fieldPath={dateFieldPath}
                exactnessFieldPath={dateProximityPath}
                fieldConfig={fieldConfig}
                fieldHtmlId={fieldHtmlId}
            >
                <div className={styles.root}>
                    <div className={styles.label}>
                        <FieldComposer.Label />
                    </div>
                    <div>
                        <FieldComposer.Inputs.Year
                            id={fieldHtmlId}
                            value={yearValue as number | null}
                            data-testid={dateFieldPath}
                            required={fieldConfig.required}
                            onChange={setBirthYear}
                        />
                        {!isRangeMode && <FieldComposer.GenericExactness />}
                    </div>
                    {hasCalc && (
                        <div className={styles.calculator}>
                            <BirthYearCalculator onSubmit={setBirthYear} />
                        </div>
                    )}
                </div>
            </FieldComposer>
        </Grid.Field>
    );
};
